import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { CvSnapshot } from "./CvSnapshot";
import { pickImage } from "../../lib/api";
import { useSite } from "@/context/SiteContext";
import { useLeadModal } from "@/context/LeadModalContext";
import { resolveBrochureUrl } from "@/lib/brochure";

const FALLBACK_PROMPTS = [
  "AI on port operations",
  "AI on banking branch operations",
  "AI on ATM fraud detection",
  "AI on PPE compliance",
];

// Hero - value prop + rotating typewriter search bar driven by `prompts` from backend.
// onSearch(q) is fired on every keystroke so the page can live-filter below.
// `results` is an array of matched features (already filtered + flattened with category info)
// for the floating dropdown panel. `onResultClick(feature)` is fired on selection.
export const Hero = ({ prompts, onSearch, query, results = [], totalMatches = 0, onResultClick }) => {
  const { settings } = useSite();
  const { open: openLeadModal } = useLeadModal();
  const brochure = resolveBrochureUrl(settings?.brochure_url);
  const list = (prompts && prompts.length ? prompts : FALLBACK_PROMPTS);

  const heroSnapshotImage =
    settings?.hero_snapshot_image_url ||
    settings?.hero_snapshot_image_data ||
    "https://images.unsplash.com/photo-1581094288338-2314dddb7ece?crop=entropy&cs=srgb&fm=jpg&q=85";
  const heroSnapshotCamId = settings?.hero_snapshot_cam_id || "CAM-04";
  const heroSnapshotBadge = settings?.hero_snapshot_badge || "OBJ · 5 · ALERT · 1";
  const heroStats = [
    { k: "Persons", v: settings?.hero_stat_persons || "05" },
    { k: "Compliance", v: settings?.hero_stat_compliance || "80%" },
    { k: "Alerts", v: settings?.hero_stat_alerts || "01" },
  ];

  const [placeholder, setPlaceholder] = useState("");
  const [promptIdx, setPromptIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [phase, setPhase] = useState("type"); // type | hold | erase
  const [focused, setFocused] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const formRef = useRef(null);
  const inputRef = useRef(null);

  const popupOpen = focused && Boolean(query.trim());
  const visibleResults = useMemo(() => results.slice(0, 8), [results]);

  // Reset highlighted item when query / results change
  useEffect(() => {
    setActiveIdx(0);
  }, [query, visibleResults.length]);

  // Close popup on outside click
  useEffect(() => {
    if (!popupOpen) return;
    const onDocDown = (e) => {
      if (formRef.current && !formRef.current.contains(e.target)) {
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [popupOpen]);

  const handleResult = (feature) => {
    setFocused(false);
    inputRef.current?.blur();
    if (onResultClick) onResultClick(feature);
  };

  const onKeyDown = (e) => {
    if (!popupOpen || visibleResults.length === 0) {
      if (e.key === "Escape") onSearch("");
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % visibleResults.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + visibleResults.length) % visibleResults.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleResult(visibleResults[activeIdx]);
    } else if (e.key === "Escape") {
      setFocused(false);
    }
  };

  // Typewriter effect - pause typing when user has typed something
  useEffect(() => {
    if (query) return; // pause while user is typing
    const current = list[promptIdx];
    let delay = 60;
    if (phase === "type") {
      if (charIdx < current.length) {
        const t = setTimeout(() => {
          setPlaceholder(current.slice(0, charIdx + 1));
          setCharIdx((c) => c + 1);
        }, delay);
        return () => clearTimeout(t);
      } else {
        const t = setTimeout(() => setPhase("hold"), 1400);
        return () => clearTimeout(t);
      }
    }
    if (phase === "hold") {
      const t = setTimeout(() => setPhase("erase"), 300);
      return () => clearTimeout(t);
    }
    if (phase === "erase") {
      if (charIdx > 0) {
        const t = setTimeout(() => {
          setPlaceholder(current.slice(0, charIdx - 1));
          setCharIdx((c) => c - 1);
        }, 32);
        return () => clearTimeout(t);
      } else {
        setPromptIdx((i) => (i + 1) % list.length);
        setPhase("type");
      }
    }
  }, [charIdx, phase, promptIdx, list, query]);

  const heroBoxes = useMemo(
    () => [
      { x: 8, y: 24, w: 14, h: 58, label: "PERSON · 99%", color: "lime", blink: false, border_style: "solid" },
      { x: 26, y: 28, w: 14, h: 54, label: "PERSON · 98%", color: "lime", blink: false, border_style: "solid" },
      { x: 44, y: 22, w: 14, h: 60, label: "PERSON · 99%", color: "lime", blink: false, border_style: "solid" },
      { x: 62, y: 30, w: 14, h: 52, label: "NO HELMET", color: "red", blink: true, border_style: "solid" },
      { x: 80, y: 26, w: 14, h: 56, label: "PERSON · 97%", color: "lime", blink: false, border_style: "solid" },
    ],
    []
  );

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-12 sm:pt-16 pb-16 sm:pb-24 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10 bg-grid bg-grid-fade" />
      <div
        aria-hidden
        className="absolute -top-32 -left-32 w-[420px] h-[420px] -z-10 rounded-full blur-3xl opacity-40"
        style={{ background: "radial-gradient(circle, rgba(132,204,22,0.35), transparent 60%)" }}
      />
      <div
        aria-hidden
        className="absolute -bottom-32 -right-32 w-[480px] h-[480px] -z-10 rounded-full blur-3xl opacity-30"
        style={{ background: "radial-gradient(circle, rgba(245,158,11,0.4), transparent 60%)" }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <motion.div
          className="lg:col-span-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-neutral-300 bg-white">
            <span className="cv-live-dot" />
            <span className="font-mono-ui text-[11px] uppercase tracking-[0.18em] text-neutral-700">
              {settings?.hero_eyebrow || "Bondstein · Smarter Spaces v1"}
            </span>
          </div>

          <h1
            className="font-display mt-6 text-[44px] sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight text-neutral-950"
            data-testid="hero-title"
          >
            {settings?.hero_title_lead || "See every space."}{" "}
            <span className="relative inline-block">
              <span className="relative z-10">
                {settings?.hero_title_highlight || "Decide in real-time."}
              </span>
              <span
                aria-hidden
                className="absolute inset-x-0 bottom-1 h-3 -z-0"
                style={{ background: "rgba(132,204,22,0.55)" }}
              />
            </span>
          </h1>

          <p
            className="mt-6 text-base sm:text-lg text-neutral-600 max-w-xl leading-relaxed"
            data-testid="hero-subtitle"
          >
            {settings?.hero_subtitle ||
              "A unified computer-vision intelligence dashboard for parking, workplace safety, banking, manufacturing and customer behaviour - engineered by Bondstein for enterprises across Bangladesh and South Asia."}
          </p>

          {/* Search */}
          <form
            ref={formRef}
            onSubmit={(e) => e.preventDefault()}
            data-testid="hero-search-form"
            className="mt-7 relative max-w-xl"
          >
            <div className="flex items-center bg-white border-2 border-neutral-200 focus-within:border-[#84CC16] rounded-xl shadow-[0_8px_24px_-12px_rgba(10,10,10,0.15)] transition-colors">
              <span className="pl-4 pr-2 text-neutral-500">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                ref={inputRef}
                type="text"
                data-testid="hero-search-input"
                value={query}
                onChange={(e) => onSearch(e.target.value)}
                onFocus={() => setFocused(true)}
                onKeyDown={onKeyDown}
                placeholder={placeholder || list[0]}
                className="flex-1 py-3 pr-3 bg-transparent text-neutral-900 placeholder:text-neutral-400 outline-none font-medium"
                autoComplete="off"
              />
              {query ? (
                <button
                  type="button"
                  data-testid="hero-search-clear"
                  onClick={() => {
                    onSearch("");
                    inputRef.current?.focus();
                  }}
                  className="mr-2 px-2 py-1 text-xs text-neutral-500 hover:text-neutral-900"
                >
                  Clear
                </button>
              ) : null}
            </div>

            {/* Floating results popup */}
            {popupOpen && (
              <div
                data-testid="hero-search-popup"
                className="absolute z-40 mt-2 left-0 right-0 bg-white border border-neutral-200 rounded-xl shadow-[0_24px_60px_-20px_rgba(10,10,10,0.25)] overflow-hidden"
              >
                <div className="px-4 py-2.5 flex items-center justify-between border-b border-neutral-100 bg-neutral-50">
                  <span
                    data-testid="hero-search-popup-count"
                    className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-neutral-600"
                  >
                    {totalMatches} match{totalMatches === 1 ? "" : "es"}
                  </span>
                  <span className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-400 hidden sm:block">
                    ↑↓ to navigate · ⏎ to open · esc to close
                  </span>
                </div>
                {visibleResults.length === 0 ? (
                  <div className="px-4 py-8 text-center" data-testid="hero-search-popup-empty">
                    <p className="font-display text-sm font-bold text-neutral-900">
                      No matches for &ldquo;{query}&rdquo;
                    </p>
                    <p className="mt-1 text-xs text-neutral-500">
                      Try a different keyword or browse all use cases below.
                    </p>
                  </div>
                ) : (
                  <ul className="max-h-[60vh] overflow-y-auto py-1">
                    {visibleResults.map((f, i) => {
                      const active = i === activeIdx;
                      const img = pickImage(f);
                      return (
                        <li key={f.id}>
                          <button
                            type="button"
                            data-testid={`hero-search-result-${f.slug || f.id}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onMouseEnter={() => setActiveIdx(i)}
                            onClick={() => handleResult(f)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                              active ? "bg-[#F7FEE7]" : "hover:bg-neutral-50"
                            }`}
                          >
                            <div
                              className="w-14 h-10 rounded-md bg-neutral-200 bg-cover bg-center flex-shrink-0 border border-neutral-200"
                              style={img ? { backgroundImage: `url(${img})` } : {}}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-display font-bold text-sm text-neutral-950 truncate">
                                  {f.title}
                                </span>
                                {active && (
                                  <span
                                    className="font-mono-ui text-[9px] uppercase tracking-[0.18em] text-neutral-500 ml-auto flex-shrink-0"
                                  >
                                    ↵
                                  </span>
                                )}
                              </div>
                              <div className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500 truncate mt-0.5">
                                {f.categoryName || "Use case"} · {f.cam_id}
                              </div>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {totalMatches > visibleResults.length && (
                  <div className="px-4 py-2 border-t border-neutral-100 bg-white">
                    <a
                      href="#use-cases"
                      onClick={() => setFocused(false)}
                      data-testid="hero-search-view-all"
                      className="text-xs font-semibold text-neutral-900 hover:text-[#65A30D]"
                    >
                      View all {totalMatches} matches ↓
                    </a>
                  </div>
                )}
              </div>
            )}

            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500 mr-1">
                Try:
              </span>
              {list.slice(0, 4).map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`hero-search-chip-${p.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => {
                    onSearch(p);
                    setFocused(true);
                    inputRef.current?.focus();
                  }}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-neutral-200 bg-white text-neutral-700 hover:border-neutral-900 hover:text-neutral-900 transition-colors"
                >
                  {p}
                </button>
              ))}
            </div>
          </form>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              type="button"
              data-testid="hero-cta-demo"
              onClick={() => openLeadModal()}
              className="inline-flex items-center justify-center px-5 py-3 rounded-md bg-[#84CC16] text-[#0A0A0A] font-semibold hover:bg-[#65A30D] transition-all duration-200 hover:shadow-[0_10px_28px_-12px_rgba(132,204,22,0.7)] hover:-translate-y-0.5"
            >
              {settings?.cta_demo_label || "Request a demo"}
              <svg className="ml-2" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <path d="M5 12h14" /><path d="M13 5l7 7-7 7" />
              </svg>
            </button>
            <a
                href={brochure}
                target="_blank"
                rel="noopener noreferrer"
                download
                data-testid="hero-cta-brochure"
                className="inline-flex items-center justify-center px-5 py-3 rounded-md border border-neutral-300 text-neutral-900 font-semibold hover:border-neutral-500 transition-colors bg-white"
              >
                <svg className="mr-2" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {settings?.cta_brochure_label || "Download brochure"}
              </a>
          </div>
        </motion.div>

        <motion.div
          className="lg:col-span-6"
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        >
          <div className="relative">
            <div className="absolute -top-3 -left-3 z-20 px-2.5 py-1 bg-[#0A0A0A] text-white font-mono-ui text-[10px] uppercase tracking-[0.16em] rounded-sm">
              Live preview · {heroSnapshotCamId}
            </div>
            <div className="absolute -bottom-3 -right-3 z-20 px-2.5 py-1 bg-[#84CC16] text-[#0A0A0A] font-mono-ui text-[10px] uppercase tracking-[0.16em] rounded-sm">
              Smarter Spaces engine
            </div>
            <div className="rounded-2xl border border-neutral-200 bg-white shadow-[0_30px_60px_-25px_rgba(10,10,10,0.25)] overflow-hidden">
              <CvSnapshot
                image={heroSnapshotImage}
                camId={heroSnapshotCamId}
                boxes={Array.isArray(settings?.hero_snapshot_boxes) && settings.hero_snapshot_boxes.length > 0 ? settings.hero_snapshot_boxes : heroBoxes}
                aspect="aspect-[16/11]"
                badge={heroSnapshotBadge}
              />
              <div className="grid grid-cols-3 divide-x divide-neutral-200 bg-white">
                {heroStats.map((s) => (
                  <div key={s.k} className="px-4 py-3">
                    <div className="font-mono-ui text-[10px] uppercase tracking-[0.16em] text-neutral-500">
                      {s.k}
                    </div>
                    <div className="font-display text-xl font-bold text-neutral-950 mt-0.5">
                      {s.v}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
